import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";

const SUPPLIER_SEARCH_MIN_LENGTH = 2;
const SUPPLIER_SEARCH_DEBOUNCE_MS = 400;
const CUSTOMER_SEARCH_MIN_LENGTH = 2;
const PO_SEARCH_DEBOUNCE_MS = 300;

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true || success === "true" || success === 1 || success === "1"
  );
}

function cleanDash(value) {
  const s = value == null ? "" : String(value).trim();
  return !s || s === "-" ? "" : s;
}

/** Normalize Mongo/string/object ids to a stable string */
function resolveEntityId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    return String(
      value.$oid || value.oid || value._id || value.id || "",
    ).trim();
  }
  return "";
}

function extractCustomersList(responseData) {
  const root = responseData?.data != null ? responseData.data : responseData;
  const list = Array.isArray(root)
    ? root
    : Array.isArray(root?.customers)
      ? root.customers
      : Array.isArray(root?.data)
        ? root.data
        : Array.isArray(responseData?.customers)
          ? responseData.customers
          : [];

  return list
    .map((item) => ({
      _id: String(item?._id || item?.id || "").trim(),
      customerName: String(
        item?.customerName || item?.customer || item?.name || "",
      ).trim(),
    }))
    .filter((item) => item._id && item.customerName)
    .sort((a, b) => a.customerName.localeCompare(b.customerName));
}

function extractPoList(payload) {
  if (Array.isArray(payload?.data?.pos)) return payload.data.pos;
  if (Array.isArray(payload?.data?.poList)) return payload.data.poList;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.pos)) return payload.pos;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function extractBalanceMaterials(payload) {
  const data = payload?.data != null ? payload.data : payload;
  if (Array.isArray(data?.materials)) return data.materials;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.materialList)) return data.materialList;
  if (Array.isArray(data)) return data;
  if (Array.isArray(payload?.materials)) return payload.materials;
  return [];
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function pickNumber(obj, ...keys) {
  if (!obj || typeof obj !== "object") return null;
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== "") {
      const n = Number(obj[key]);
      if (Number.isFinite(n)) return n;
    }
  }
  const entries = Object.entries(obj);
  for (const key of keys) {
    const want = String(key).toLowerCase();
    const found = entries.find(([k]) => String(k).toLowerCase() === want);
    if (found && found[1] != null && found[1] !== "") {
      const n = Number(found[1]);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function calcFreightLinePricing(basicPrice, gstPercent, freightQty) {
  const qty = Number(freightQty);
  const bp = Number(basicPrice);
  const gst = Number(gstPercent);
  if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(bp)) {
    return {
      grossBasicPrice: null,
      gstValue: null,
      totalValue: null,
    };
  }
  const grossBasicPrice = bp * qty;
  const gstValue = Number.isFinite(gst)
    ? (grossBasicPrice * gst) / 100
    : null;
  const totalValue =
    gstValue != null ? grossBasicPrice + gstValue : grossBasicPrice;
  return { grossBasicPrice, gstValue, totalValue };
}

function mapPoLineToPicker(m, index, poHeaderId, poNumber, forcedPoMaterialId) {
  const code = cleanDash(m?.materialCode || m?.code);
  const description =
    m?.materialDescription || m?.productName || m?.description || "";
  const poQty =
    pickNumber(m, "poQuantity", "quantity", "qty") ??
    (m?.poQuantity ?? m?.quantity ?? m?.qty ?? "");
  const balance =
    pickNumber(m, "balanceQuantity", "poBalanceQuantity", "remaining") ??
    (m?.balanceQuantity ?? m?.poBalanceQuantity ?? m?.remaining ?? poQty);
  const linePoId = resolveEntityId(m?.po_id || m?.poId) || String(poHeaderId || "").trim();
  const linePoNumber = String(m?.poNumber || poNumber || "").trim();
  // Identity always comes from get-all-po material _id (+ index as uniqueness safety)
  const poMaterialId =
    resolveEntityId(forcedPoMaterialId) ||
    resolveEntityId(m?.poMaterialId) ||
    resolveEntityId(m?._id) ||
    resolveEntityId(m?.id);
  const lineKey = `pom-${linePoId}-${index}-${poMaterialId || "noid"}`;

  const basicPrice = pickNumber(m, "basicPrice", "basic_price");
  const gstPercent = pickNumber(m, "gstPercent", "gst", "gst_percent");
  const qtyNum = pickNumber(m, "poQuantity", "quantity", "qty");
  let grossBasicPrice = pickNumber(
    m,
    "grossbasicPrice",
    "grossBasicPrice",
    "grossValue",
    "gross_basic_price",
  );
  let gstValue = pickNumber(m, "gstValue", "gst_value");
  let totalValue = pickNumber(m, "totalValue", "total_value");

  // Fallback from basicPrice × PO qty when API omits computed fields
  if (
    grossBasicPrice == null &&
    basicPrice != null &&
    qtyNum != null &&
    qtyNum > 0
  ) {
    grossBasicPrice = basicPrice * qtyNum;
  }
  if (
    gstValue == null &&
    grossBasicPrice != null &&
    gstPercent != null
  ) {
    gstValue = (grossBasicPrice * gstPercent) / 100;
  }
  if (totalValue == null && grossBasicPrice != null) {
    totalValue =
      gstValue != null ? grossBasicPrice + gstValue : grossBasicPrice;
  }

  const reserved = pickNumber(m, "reservedTotal", "reserved_total");

  return {
    lineKey: String(lineKey),
    pickerIndex: index,
    po_id: linePoId,
    poMaterialId,
    poNumber: linePoNumber,
    materialCode: code,
    materialDescription: description,
    hsnCode: cleanDash(m?.hsnCode || m?.hsn),
    uom: cleanDash(m?.uom),
    poQuantity: poQty === "" || poQty == null ? "" : String(poQty),
    balanceQuantity: balance === "" || balance == null ? "" : String(balance),
    reservedTotal: reserved != null ? String(reserved) : "",
    basicPrice,
    grossBasicPrice,
    gstPercent,
    gstValue,
    totalValue,
    isFullyBooked: Boolean(m?.isFullyBooked),
  };
}

/** Pull only balance/reserved fields from freight-balance API — never identity */
function pickBalanceFieldsFromResponse(responseData, poMaterialId) {
  const data =
    responseData?.data && typeof responseData.data === "object"
      ? responseData.data
      : {};
  const list = Array.isArray(data.materials)
    ? data.materials
    : extractBalanceMaterials(responseData);

  const want = resolveEntityId(poMaterialId);
  let source = null;
  if (Array.isArray(list) && list.length > 0) {
    if (want) {
      source =
        list.find(
          (item) =>
            resolveEntityId(item?.poMaterialId) === want ||
            resolveEntityId(item?._id) === want ||
            resolveEntityId(item?.id) === want,
        ) || null;
    }
    // Per-material API often returns a single-item list without matching ids
    if (!source && list.length === 1) source = list[0];
  }
  if (!source && data.material && typeof data.material === "object") {
    source = data.material;
  }
  if (!source) source = data;

  const balance =
    pickNumber(source, "balanceQuantity", "poBalanceQuantity", "remaining") ??
    null;
  const reserved = pickNumber(source, "reservedTotal", "reserved_total");
  const isFullyBooked = Boolean(source?.isFullyBooked);

  return {
    balanceQuantity: balance,
    reservedTotal: reserved,
    isFullyBooked,
  };
}

function makeRowKey() {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const FrightTrackerCreate = () => {
  const navigate = useNavigate();

  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);

  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierResults, setSupplierResults] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [isSearchingSupplier, setIsSearchingSupplier] = useState(false);

  const [freightDescription, setFreightDescription] = useState("");
  const [freightAmount, setFreightAmount] = useState("");
  const [remarks, setRemarks] = useState("");

  const [poSearchTerm, setPoSearchTerm] = useState("");
  const [poResults, setPoResults] = useState([]);
  const [showPoDropdown, setShowPoDropdown] = useState(false);
  const [isSearchingPo, setIsSearchingPo] = useState(false);
  const [isLoadingPoDetail, setIsLoadingPoDetail] = useState(false);
  const [poPickerMaterials, setPoPickerMaterials] = useState([]);
  const [selectedPoNumber, setSelectedPoNumber] = useState("");

  const [materials, setMaterials] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextFreightNumber, setNextFreightNumber] = useState("");
  const [isLoadingNextFreightNumber, setIsLoadingNextFreightNumber] =
    useState(true);

  const customerPanelRef = useRef(null);
  const supplierPanelRef = useRef(null);
  const poPanelRef = useRef(null);
  const customerDebounceRef = useRef(null);
  const supplierDebounceRef = useRef(null);
  const poDebounceRef = useRef(null);
  const customerRequestIdRef = useRef(0);
  const supplierRequestIdRef = useRef(0);
  const poRequestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const fetchNextFreightNumber = async () => {
      setIsLoadingNextFreightNumber(true);
      try {
        const currentUserId = localStorage.getItem("id") || undefined;
        const response = await axios.post(API_URL, {
          action: "get-next-freight-number",
          currentUserId,
        });
        if (cancelled) return;

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (isApiSuccess(responseData)) {
          const next =
            responseData?.data?.nextFreightNumber ||
            responseData?.nextFreightNumber ||
            "";
          setNextFreightNumber(String(next).trim());
        } else {
          setNextFreightNumber("");
          toast.error(
            responseData?.message || "Failed to fetch next freight number",
            { autoClose: 2000 },
          );
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Next freight number error:", error);
        setNextFreightNumber("");
        toast.error("Failed to fetch next freight number", { autoClose: 2000 });
      } finally {
        if (!cancelled) setIsLoadingNextFreightNumber(false);
      }
    };

    fetchNextFreightNumber();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        customerPanelRef.current &&
        !customerPanelRef.current.contains(e.target)
      ) {
        setShowCustomerDropdown(false);
      }
      if (
        supplierPanelRef.current &&
        !supplierPanelRef.current.contains(e.target)
      ) {
        setShowSupplierDropdown(false);
      }
      if (poPanelRef.current && !poPanelRef.current.contains(e.target)) {
        setShowPoDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(
    () => () => {
      if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
      if (supplierDebounceRef.current) clearTimeout(supplierDebounceRef.current);
      if (poDebounceRef.current) clearTimeout(poDebounceRef.current);
    },
    [],
  );

  const searchCustomers = useCallback(async (term) => {
    const search = String(term || "").trim();
    if (search.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      setCustomerResults([]);
      return;
    }
    const requestId = ++customerRequestIdRef.current;
    setIsSearchingCustomer(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-customers",
        search,
      });
      if (requestId !== customerRequestIdRef.current) return;
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        setCustomerResults(extractCustomersList(responseData));
      } else {
        setCustomerResults([]);
      }
    } catch (error) {
      if (requestId !== customerRequestIdRef.current) return;
      console.error("Customer search error:", error);
      setCustomerResults([]);
    } finally {
      if (requestId === customerRequestIdRef.current) {
        setIsSearchingCustomer(false);
      }
    }
  }, []);

  const searchSuppliers = useCallback(async (term) => {
    const search = String(term || "").trim();
    if (search.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierResults([]);
      return;
    }
    const requestId = ++supplierRequestIdRef.current;
    setIsSearchingSupplier(true);
    try {
      const userRole = localStorage.getItem("role");
      const payload = { action: "get-suppliers", status: "active", search };
      if (userRole !== "admin") {
        payload.status = "active";
      }
      const response = await axios.post(API_URL, payload);
      if (requestId !== supplierRequestIdRef.current) return;
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const list = Array.isArray(responseData?.suppliers)
        ? responseData.suppliers
        : Array.isArray(responseData?.data?.suppliers)
          ? responseData.data.suppliers
          : [];
      setSupplierResults(isApiSuccess(responseData) ? list : []);
    } catch (error) {
      if (requestId !== supplierRequestIdRef.current) return;
      console.error("Supplier search error:", error);
      setSupplierResults([]);
    } finally {
      if (requestId === supplierRequestIdRef.current) {
        setIsSearchingSupplier(false);
      }
    }
  }, []);

  const searchPurchaseOrders = useCallback(async (term) => {
    const q = String(term || "").trim();
    if (!q) {
      setPoResults([]);
      return;
    }
    const requestId = ++poRequestIdRef.current;
    setIsSearchingPo(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-po",
        page: 1,
        limit: 20,
        poStatus: "Approved",
        search: q,
        status: "active",
      });
      if (requestId !== poRequestIdRef.current) return;
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      setPoResults(isApiSuccess(responseData) ? extractPoList(responseData) : []);
    } catch (error) {
      if (requestId !== poRequestIdRef.current) return;
      console.error("PO search error:", error);
      setPoResults([]);
    } finally {
      if (requestId === poRequestIdRef.current) {
        setIsSearchingPo(false);
      }
    }
  }, []);

  const handleCustomerSearchChange = (value) => {
    setCustomerSearch(value);
    if (customerId) {
      setCustomerId("");
      setCustomerName("");
    }
    customerRequestIdRef.current += 1;
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
    const trimmed = value.trim();
    if (trimmed.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      setCustomerResults([]);
      setShowCustomerDropdown(false);
      setIsSearchingCustomer(false);
      return;
    }
    setShowCustomerDropdown(true);
    customerDebounceRef.current = setTimeout(() => {
      searchCustomers(trimmed);
    }, 350);
  };

  const handleSelectCustomer = (customer) => {
    setCustomerId(customer._id);
    setCustomerName(customer.customerName);
    setCustomerSearch(customer.customerName);
    setCustomerResults([]);
    setShowCustomerDropdown(false);
  };

  const handleSupplierSearchChange = (value) => {
    setSupplierSearch(value);
    supplierRequestIdRef.current += 1;
    if (supplierDebounceRef.current) clearTimeout(supplierDebounceRef.current);
    const trimmed = value.trim();
    if (trimmed.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierResults([]);
      setShowSupplierDropdown(false);
      setIsSearchingSupplier(false);
      return;
    }
    setShowSupplierDropdown(true);
    supplierDebounceRef.current = setTimeout(() => {
      searchSuppliers(trimmed);
    }, SUPPLIER_SEARCH_DEBOUNCE_MS);
  };

  const handleSelectSupplier = (supplier) => {
    const sid = String(supplier?.id || supplier?._id || "").trim();
    const name = String(
      supplier?.clientname || supplier?.clientName || supplier?.name || "",
    ).trim();
    if (!sid || !name) {
      toast.error("Selected supplier is incomplete", { autoClose: 2000 });
      return;
    }
    setSelectedSuppliers((prev) => {
      if (prev.some((s) => s.supplierId === sid)) return prev;
      return [...prev, { supplierId: sid, supplierName: name }];
    });
    setSupplierSearch("");
    setSupplierResults([]);
    setShowSupplierDropdown(false);
  };

  const handleRemoveSupplier = (supplierId) => {
    setSelectedSuppliers((prev) =>
      prev.filter((s) => s.supplierId !== supplierId),
    );
  };

  const handlePoSearchChange = (value) => {
    setPoSearchTerm(value);
    poRequestIdRef.current += 1;
    if (poDebounceRef.current) clearTimeout(poDebounceRef.current);
    const trimmed = value.trim();
    if (!trimmed) {
      setPoResults([]);
      setShowPoDropdown(false);
      setIsSearchingPo(false);
      return;
    }
    setShowPoDropdown(true);
    poDebounceRef.current = setTimeout(() => {
      searchPurchaseOrders(trimmed);
    }, PO_SEARCH_DEBOUNCE_MS);
  };

  const applyPoSelection = async (po) => {
    const poId = resolveEntityId(po?._id || po?.id);
    if (!poId) {
      toast.error("Selected PO has no id", { autoClose: 2000 });
      return;
    }

    setIsLoadingPoDetail(true);
    setShowPoDropdown(false);
    const poNo = po.poNumber || po.poNo || "";
    setPoSearchTerm(poNo);
    setSelectedPoNumber(poNo);

    const poMaterials = Array.isArray(po?.materials) ? po.materials : [];
    if (poMaterials.length === 0) {
      setPoPickerMaterials([]);
      toast.warning("This PO has no material lines", { autoClose: 2500 });
      setIsLoadingPoDetail(false);
      return;
    }

    // Build unique picker rows from get-all-po materials first (identity = material._id)
    const baseRows = poMaterials.map((poMaterial, index) => {
      const poMaterialId = resolveEntityId(poMaterial?._id || poMaterial?.id);
      return mapPoLineToPicker(poMaterial, index, poId, poNo, poMaterialId);
    });
    setPoPickerMaterials(baseRows);

    try {
      const currentUserId = localStorage.getItem("id") || undefined;

      // Enrich each row with freight balance only — do not remake identity
      const enrichedRows = await Promise.all(
        baseRows.map(async (baseRow) => {
          const poMaterialId = resolveEntityId(baseRow.poMaterialId);
          if (!poMaterialId) return baseRow;

          try {
            const response = await axios.post(API_URL, {
              action: "get-po-material-freight-balance",
              poId,
              poMaterialId,
              currentUserId,
            });
            const responseData = normalizeApiPayload(
              parseApiResponse(response.data),
            );

            if (!isApiSuccess(responseData)) {
              console.warn(
                "Freight balance failed for poMaterialId:",
                poMaterialId,
                responseData?.message,
              );
              return baseRow;
            }

            const balanceFields = pickBalanceFieldsFromResponse(
              responseData,
              poMaterialId,
            );
            return {
              ...baseRow,
              balanceQuantity:
                balanceFields.balanceQuantity != null
                  ? String(balanceFields.balanceQuantity)
                  : baseRow.balanceQuantity,
              reservedTotal:
                balanceFields.reservedTotal != null
                  ? String(balanceFields.reservedTotal)
                  : baseRow.reservedTotal,
              isFullyBooked: balanceFields.isFullyBooked,
            };
          } catch (err) {
            console.error(
              "PO freight balance error for material:",
              poMaterialId,
              err,
            );
            return baseRow;
          }
        }),
      );

      setPoPickerMaterials(enrichedRows);
    } catch (error) {
      console.error("PO freight balance error:", error);
      toast.error("Failed to load PO freight balance", { autoClose: 2500 });
      // Keep base rows so both material lines remain selectable
    } finally {
      setIsLoadingPoDetail(false);
    }
  };

  const isMaterialAlreadyAdded = (line) => {
    const lineId = resolveEntityId(line?.poMaterialId);
    if (lineId) {
      return materials.some(
        (row) => resolveEntityId(row?.poMaterialId) === lineId,
      );
    }
    // Fallback when id missing: match exact picker lineKey (includes index)
    const key = String(line?.lineKey || "").trim();
    if (!key) return false;
    return materials.some((row) => String(row?.lineKey || "").trim() === key);
  };

  const handleAddMaterial = (line) => {
    if (isMaterialAlreadyAdded(line)) {
      toast.info("Material already added", { autoClose: 2000 });
      return;
    }
    const poMaterialId = resolveEntityId(line.poMaterialId);
    setMaterials((prev) => [
      ...prev,
      {
        rowKey: makeRowKey(),
        lineKey: line.lineKey,
        pickerIndex: line.pickerIndex,
        po_id: line.po_id,
        poMaterialId,
        poNumber: line.poNumber,
        materialCode: line.materialCode,
        materialDescription: line.materialDescription,
        hsnCode: line.hsnCode || "",
        uom: line.uom || "",
        balanceQuantity: line.balanceQuantity,
        basicPrice: line.basicPrice,
        gstPercent: line.gstPercent,
        freightQty: "",
        grossBasicPrice: null,
        gstValue: null,
        totalValue: null,
      },
    ]);
  };

  const handleRemoveMaterial = (rowKey) => {
    setMaterials((prev) => prev.filter((row) => row.rowKey !== rowKey));
  };

  const handleFreightQtyChange = (rowKey, value) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setMaterials((prev) =>
      prev.map((row) => {
        if (row.rowKey !== rowKey) return row;
        const pricing = calcFreightLinePricing(
          row.basicPrice,
          row.gstPercent,
          value,
        );
        return {
          ...row,
          freightQty: value,
          grossBasicPrice: pricing.grossBasicPrice,
          gstValue: pricing.gstValue,
          totalValue: pricing.totalValue,
        };
      }),
    );
  };

  const getFreightQtyError = (row) => {
    const raw = String(row?.freightQty ?? "").trim();
    if (!raw) return "";
    const qty = Number(raw);
    if (!Number.isFinite(qty)) return "Enter a valid freight qty";
    if (qty <= 0) return "Freight qty must be greater than 0";
    const balance = Number(row?.balanceQuantity);
    if (Number.isFinite(balance) && qty > balance) {
      return `Freight qty cannot exceed balance (${balance})`;
    }
    return "";
  };

  const hasFreightQtyErrors = materials.some((row) =>
    Boolean(getFreightQtyError(row)),
  );

  const materialsTotalPoValue = materials.reduce((sum, row) => {
    const total = Number(row.totalValue);
    return sum + (Number.isFinite(total) ? total : 0);
  }, 0);

  const validateForm = () => {
    if (!nextFreightNumber.trim()) {
      toast.error("Freight number is not available. Please refresh.", {
        autoClose: 2000,
      });
      return false;
    }
    if (!customerId || !customerName.trim()) {
      toast.error("Please select a customer", { autoClose: 2000 });
      return false;
    }
    if (!freightDescription.trim()) {
      toast.error("Freight description is required", { autoClose: 2000 });
      return false;
    }
    const amount = Number(freightAmount);
    if (!String(freightAmount).trim() || !Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid freight amount", { autoClose: 2000 });
      return false;
    }
    if (selectedSuppliers.length === 0) {
      toast.error("Please select at least one supplier", { autoClose: 2000 });
      return false;
    }
    if (
      selectedSuppliers.some(
        (s) => !String(s.supplierId || "").trim() || !String(s.supplierName || "").trim(),
      )
    ) {
      toast.error("One or more selected suppliers are incomplete", {
        autoClose: 2000,
      });
      return false;
    }
    if (materials.length === 0) {
      toast.error("Add at least one material", { autoClose: 2000 });
      return false;
    }
    for (let i = 0; i < materials.length; i++) {
      const row = materials[i];
      if (!row.po_id) {
        toast.error(`Material row ${i + 1}: PO is missing`, { autoClose: 2000 });
        return false;
      }
      if (!row.materialCode && !row.materialDescription) {
        toast.error(`Material row ${i + 1}: material is incomplete`, {
          autoClose: 2000,
        });
        return false;
      }
      const qty = Number(row.freightQty);
      if (!String(row.freightQty).trim() || !Number.isFinite(qty) || qty <= 0) {
        toast.error(`Material row ${i + 1}: enter a valid freight qty`, {
          autoClose: 2000,
        });
        return false;
      }
      if (getFreightQtyError(row)) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const materialPayload = materials.map((row) => {
        const pricing = calcFreightLinePricing(
          row.basicPrice,
          row.gstPercent,
          row.freightQty,
        );
        const quoteRow = {
          po_id: row.po_id,
          materialCode: row.materialCode,
          materialDescription: row.materialDescription,
          freightQty: Number(row.freightQty),
          basicPrice: Number(row.basicPrice) || 0,
          grossbasicPrice: Number(pricing.grossBasicPrice) || 0,
          gstPercent: Number(row.gstPercent) || 0,
          gstValue: Number(pricing.gstValue) || 0,
          totalValue: Number(pricing.totalValue) || 0,
        };
        if (row.poMaterialId) {
          quoteRow.poMaterialId = row.poMaterialId;
        }
        return quoteRow;
      });

      const totalPoValue = materialPayload.reduce(
        (sum, row) => sum + (Number(row.totalValue) || 0),
        0,
      );

      const payload = {
        action: "create-freight",
        freightNumber: nextFreightNumber.trim(),
        customerName: customerName.trim(),
        customerId,
        freightDescription: freightDescription.trim(),
        freightAmount: Number(freightAmount),
        suppliers: selectedSuppliers.map((s) => ({
          supplierName: String(s.supplierName).trim(),
          supplierId: String(s.supplierId).trim(),
        })),
        remarks: remarks.trim(),
        currentUserId,
        createdBy: currentUserId,
        totalPoValue,
        materials: materialPayload,
      };

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData.message || "Freight created successfully",
          { autoClose: 2000 },
        );
        setTimeout(() => {
          navigate("/frightTracker", { replace: true });
        }, 1200);
      } else {
        toast.error(responseData?.message || "Failed to create freight", {
          autoClose: 2500,
        });
      }
    } catch (error) {
      console.error("Create freight error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while creating freight",
        { autoClose: 2500 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSupplierLabel = (s) =>
    String(s?.clientname || s?.clientName || s?.name || "").trim();

  const availableSupplierResults = supplierResults.filter((s) => {
    const sid = String(s?.id || s?._id || "").trim();
    return (
      sid && !selectedSuppliers.some((x) => x.supplierId === sid)
    );
  });

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Create Freight</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/frightTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Freight Number
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            value={
                              isLoadingNextFreightNumber
                                ? "Fetching next freight number..."
                                : nextFreightNumber || "-"
                            }
                            disabled
                            readOnly
                          />
                          <small className="text-muted">
                            Auto-generated freight number for this record.
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Customer <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="position-relative" ref={customerPanelRef}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search customer name..."
                              value={customerSearch}
                              onChange={(e) =>
                                handleCustomerSearchChange(e.target.value)
                              }
                              onFocus={() => {
                                if (
                                  customerSearch.trim().length >=
                                  CUSTOMER_SEARCH_MIN_LENGTH
                                ) {
                                  setShowCustomerDropdown(true);
                                }
                              }}
                              disabled={isSubmitting}
                              autoComplete="off"
                            />
                            {showCustomerDropdown && (
                              <div
                                className="list-group position-absolute w-100"
                                style={{
                                  zIndex: 30,
                                  maxHeight: "220px",
                                  overflowY: "auto",
                                  top: "100%",
                                  left: 0,
                                }}
                              >
                                {isSearchingCustomer ? (
                                  <div className="list-group-item text-muted">
                                    Searching...
                                  </div>
                                ) : customerResults.length === 0 ? (
                                  <div className="list-group-item text-muted">
                                    No customers found
                                  </div>
                                ) : (
                                  customerResults.map((c) => (
                                    <button
                                      key={c._id}
                                      type="button"
                                      className="list-group-item list-group-item-action"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelectCustomer(c);
                                      }}
                                    >
                                      {c.customerName}
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                          <small className="text-muted">
                            Type at least {CUSTOMER_SEARCH_MIN_LENGTH} characters
                            to search.
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Freight Description{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <textarea
                            className="form-control"
                            rows={2}
                            placeholder="e.g. Road freight - Chennai to Pune"
                            value={freightDescription}
                            onChange={(e) =>
                              setFreightDescription(e.target.value)
                            }
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Freight Amount <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={freightAmount}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "" || /^\d*\.?\d*$/.test(v)) {
                                setFreightAmount(v);
                              }
                            }}
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Supplier <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          {selectedSuppliers.length > 0 && (
                            <div className="mb-2">
                              {selectedSuppliers.map((s) => (
                                <span
                                  key={s.supplierId}
                                  className="badge badge-info mr-2 mb-1"
                                  style={{ fontSize: "12px", fontWeight: 550 }}
                                >
                                  {s.supplierName}
                                  <span
                                    className="ml-2 crsr_pntr"
                                    onMouseDown={(ev) => {
                                      ev.preventDefault();
                                      if (!isSubmitting) {
                                        handleRemoveSupplier(s.supplierId);
                                      }
                                    }}
                                    title="Remove"
                                  >
                                    <i className="fa-solid fa-xmark" />
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="position-relative" ref={supplierPanelRef}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search and add suppliers..."
                              value={supplierSearch}
                              onChange={(e) =>
                                handleSupplierSearchChange(e.target.value)
                              }
                              onFocus={() => {
                                if (
                                  supplierSearch.trim().length >=
                                  SUPPLIER_SEARCH_MIN_LENGTH
                                ) {
                                  setShowSupplierDropdown(true);
                                }
                              }}
                              disabled={isSubmitting}
                              autoComplete="off"
                            />
                            {showSupplierDropdown && (
                              <div
                                className="list-group position-absolute w-100"
                                style={{
                                  zIndex: 30,
                                  maxHeight: "220px",
                                  overflowY: "auto",
                                  top: "100%",
                                  left: 0,
                                }}
                              >
                                {isSearchingSupplier ? (
                                  <div className="list-group-item text-muted">
                                    Searching...
                                  </div>
                                ) : availableSupplierResults.length === 0 ? (
                                  <div className="list-group-item text-muted">
                                    No suppliers found
                                  </div>
                                ) : (
                                  availableSupplierResults.map((s) => {
                                    const sid = String(
                                      s?.id || s?._id || "",
                                    ).trim();
                                    const label = getSupplierLabel(s);
                                    return (
                                      <button
                                        key={sid || label}
                                        type="button"
                                        className="list-group-item list-group-item-action"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleSelectSupplier(s);
                                        }}
                                      >
                                        {label || sid}
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                          <small className="text-muted">
                            Type at least {SUPPLIER_SEARCH_MIN_LENGTH} characters
                            to search. You can select multiple suppliers.
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Remarks
                        </label>
                        <div className="col-12 col-md-8">
                          <textarea
                            className="form-control"
                            rows={2}
                            placeholder="Optional remarks"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>

                      <div className="row mb-2 mt-4">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2 mb-3">
                            <i className="fa-solid fa-boxes-stacked mr-2" />
                            Materials <span className="text-danger">*</span>
                          </h5>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Search PO
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="position-relative" ref={poPanelRef}>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search approved PO number..."
                                value={poSearchTerm}
                                onChange={(e) =>
                                  handlePoSearchChange(e.target.value)
                                }
                                onFocus={() => {
                                  if (poSearchTerm.trim()) {
                                    setShowPoDropdown(true);
                                  }
                                }}
                                disabled={isSubmitting || isLoadingPoDetail}
                                autoComplete="off"
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">
                                  {isSearchingPo || isLoadingPoDetail ? (
                                    <i className="fa-solid fa-spinner fa-spin" />
                                  ) : (
                                    <i className="fa-solid fa-search" />
                                  )}
                                </span>
                              </div>
                            </div>
                            {showPoDropdown && (
                              <div
                                className="list-group position-absolute w-100"
                                style={{
                                  zIndex: 30,
                                  maxHeight: "220px",
                                  overflowY: "auto",
                                  top: "100%",
                                  left: 0,
                                }}
                              >
                                {isSearchingPo ? (
                                  <div className="list-group-item text-muted">
                                    Searching...
                                  </div>
                                ) : poResults.length === 0 ? (
                                  <div className="list-group-item text-muted">
                                    No POs found
                                  </div>
                                ) : (
                                  poResults.map((po) => {
                                    const pid = String(po?._id || po?.id || "");
                                    const pno =
                                      po?.poNumber || po?.poNo || pid;
                                    return (
                                      <button
                                        key={pid || pno}
                                        type="button"
                                        className="list-group-item list-group-item-action"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          applyPoSelection(po);
                                        }}
                                      >
                                        <strong>{pno}</strong>
                                        {po?.supplierName ? (
                                          <span className="text-muted ml-2">
                                            {po.supplierName}
                                          </span>
                                        ) : null}
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                          {selectedPoNumber ? (
                            <small className="text-muted">
                              Selected PO: {selectedPoNumber}
                            </small>
                          ) : (
                            <small className="text-muted">
                              Select a PO to load materials, then add lines below.
                            </small>
                          )}
                        </div>
                      </div>

                      {poPickerMaterials.length > 0 && (
                        <div className="table-responsive mb-3">
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr
                                style={{
                                  backgroundColor: "#003366",
                                  color: "#fff",
                                }}
                              >
                                <th style={{ fontSize: "12px" }}>Material Code</th>
                                <th style={{ fontSize: "12px" }}>Description</th>
                                <th style={{ fontSize: "12px" }}>PO Qty</th>
                                <th style={{ fontSize: "12px" }}>Reserved</th>
                                <th style={{ fontSize: "12px" }}>Balance</th>
                                <th style={{ fontSize: "12px" }}>Basic Price</th>
                                <th style={{ fontSize: "12px" }}>
                                  Gross Basic
                                </th>
                                <th style={{ fontSize: "12px" }}>GST %</th>
                                <th style={{ fontSize: "12px" }}>GST Value</th>
                                <th style={{ fontSize: "12px" }}>Total Value</th>
                                <th style={{ fontSize: "12px", width: "90px" }}>
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {poPickerMaterials.map((line, pickerIdx) => {
                                const added = isMaterialAlreadyAdded(line);
                                const noBalance =
                                  line.isFullyBooked ||
                                  Number(line.balanceQuantity) <= 0;
                                return (
                                  <tr
                                    key={
                                      line.lineKey ||
                                      `picker-${pickerIdx}-${line.poMaterialId || ""}`
                                    }
                                  >
                                    <td>{line.materialCode || "-"}</td>
                                    <td>{line.materialDescription || "-"}</td>
                                    <td>{line.poQuantity || "-"}</td>
                                    <td>{line.reservedTotal || "0"}</td>
                                    <td>{line.balanceQuantity || "0"}</td>
                                    <td className="text-right">
                                      {formatAmount(line.basicPrice)}
                                    </td>
                                    <td className="text-right">
                                      {formatAmount(line.grossBasicPrice)}
                                    </td>
                                    <td className="text-right">
                                      {Number.isFinite(Number(line.gstPercent))
                                        ? Number(line.gstPercent)
                                        : "-"}
                                    </td>
                                    <td className="text-right">
                                      {formatAmount(line.gstValue)}
                                    </td>
                                    <td className="text-right fwt_600">
                                      {formatAmount(line.totalValue)}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-blue btn-sm"
                                        disabled={
                                          added || noBalance || isSubmitting
                                        }
                                        title={
                                          added
                                            ? "Already added"
                                            : noBalance
                                              ? "No balance available"
                                              : `Add material (${line.poMaterialId || pickerIdx})`
                                        }
                                        onClick={() => handleAddMaterial(line)}
                                      >
                                        {added
                                          ? "Added"
                                          : noBalance
                                            ? "Full"
                                            : "Add"}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {materials.length === 0 ? (
                        <div className="txtdsply text-muted mb-3">
                          No materials added yet.
                        </div>
                      ) : (
                        <div className="table-responsive mb-3">
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr
                                style={{
                                  backgroundColor: "#003366",
                                  color: "#fff",
                                }}
                              >
                                <th style={{ fontSize: "12px" }}>S.No</th>
                                <th style={{ fontSize: "12px" }}>PO Number</th>
                                <th style={{ fontSize: "12px" }}>
                                  Material Code
                                </th>
                                <th style={{ fontSize: "12px" }}>
                                  Material Description
                                </th>
                                <th style={{ fontSize: "12px" }}>Basic Price</th>
                                <th
                                  style={{
                                    fontSize: "12px",
                                    width: "110px",
                                  }}
                                >
                                  Freight Qty *
                                </th>
                                <th style={{ fontSize: "12px" }}>
                                  Gross Basic
                                </th>
                                <th style={{ fontSize: "12px" }}>GST %</th>
                                <th style={{ fontSize: "12px" }}>GST Value</th>
                                <th style={{ fontSize: "12px" }}>Total Value</th>
                                <th style={{ fontSize: "12px", width: "70px" }}>
                                  Remove
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {materials.map((row, index) => {
                                const qtyError = getFreightQtyError(row);
                                return (
                                <tr key={row.rowKey}>
                                  <td>{index + 1}</td>
                                  <td>{row.poNumber || "-"}</td>
                                  <td>{row.materialCode || "-"}</td>
                                  <td>{row.materialDescription || "-"}</td>
                                  <td className="text-right">
                                    {formatAmount(row.basicPrice)}
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className={`form-control form-control-sm${
                                        qtyError ? " is-invalid" : ""
                                      }`}
                                      inputMode="decimal"
                                      value={row.freightQty}
                                      onChange={(e) =>
                                        handleFreightQtyChange(
                                          row.rowKey,
                                          e.target.value,
                                        )
                                      }
                                      disabled={isSubmitting}
                                      style={{ maxWidth: "120px" }}
                                    />
                                    {qtyError ? (
                                      <div
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "11px" }}
                                      >
                                        {qtyError}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="text-right">
                                    {formatAmount(row.grossBasicPrice)}
                                  </td>
                                  <td className="text-right">
                                    {Number.isFinite(Number(row.gstPercent))
                                      ? Number(row.gstPercent)
                                      : "-"}
                                  </td>
                                  <td className="text-right">
                                    {formatAmount(row.gstValue)}
                                  </td>
                                  <td className="text-right fwt_600">
                                    {formatAmount(row.totalValue)}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() =>
                                        handleRemoveMaterial(row.rowKey)
                                      }
                                      disabled={isSubmitting}
                                    >
                                      <i className="fa-solid fa-trash" />
                                    </button>
                                  </td>
                                </tr>
                                );
                              })}
                              <tr style={{ backgroundColor: "#f4f6f9" }}>
                                <td
                                  colSpan={9}
                                  className="text-right fwt_600 align-middle"
                                >
                                  Total PO Value
                                </td>
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(materialsTotalPoValue)}
                                </td>
                                <td />
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isSubmitting || hasFreightQtyErrors}
                      >
                        {isSubmitting ? "Creating..." : "Create Freight"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrightTrackerCreate;
